import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Radio,
  Slider,
  Typography,
} from "@material-ui/core";
import useScoreKeyboardShortcuts from "../useScoreKeyboardShortcuts";
import { useStyles } from "./index.styles";

const MIN_SCORE = 0;
const MAX_SCORE = 10;

/**
 * The scoring input + navigation controls for a single read (AO-33366), laid out
 * per the mockup: a "Missing or incorrect characters" slider with its value to the
 * right, an Exception radio, a "N Remaining" progress bar, and the
 * Previous / Skip / Save/Next button row.
 *
 * Owns the pure UI score state and enforces mutual exclusion (Req 4.4/4.5):
 * selecting Exception resets the slider to 0; moving the slider clears Exception.
 * When navigating to a read with a previously saved score, the controls reflect
 * that saved value (Req 5.3).
 */
const LPRScoringControls = ({
  lprId,
  savedScore,
  remaining,
  scored,
  isFirst,
  isSaving,
  hasCurrentRead,
  canSkip,
  canSave,
  onPrevious,
  onSkip,
  onSaveNext,
}) => {
  const classes = useStyles();

  const [incorrectCharacters, setIncorrectCharacters] = useState(MIN_SCORE);
  const [isException, setIsException] = useState(false);

  useEffect(() => {
    if (savedScore) {
      setIsException(Boolean(savedScore.isException));
      setIncorrectCharacters(
        savedScore.isException ? MIN_SCORE : savedScore.incorrectCharacters ?? MIN_SCORE
      );
    } else {
      setIsException(false);
      setIncorrectCharacters(MIN_SCORE);
    }
  }, [lprId, savedScore, hasCurrentRead]);

  const handleSliderChange = (_, value) => {
    setIncorrectCharacters(value);
    if (isException) {
      setIsException(false);
    }
  };

  const handleExceptionClick = () => {
    setIsException((prev) => {
      const next = !prev;
      if (next) {
        setIncorrectCharacters(MIN_SCORE);
      }
      return next;
    });
  };

  const setScore = useCallback((value) => {
    setIncorrectCharacters(value);
    setIsException(false);
  }, []);

  const handleSaveNext = useCallback(() => {
    onSaveNext({
      incorrectCharacters: isException ? null : incorrectCharacters,
      isException,
    });
  }, [onSaveNext, isException, incorrectCharacters]);

  useScoreKeyboardShortcuts({
    enabled: hasCurrentRead,
    canSave: hasCurrentRead && !isSaving && canSave,
    onSetScore: setScore,
    onSave: handleSaveNext,
  });

  const total = scored + remaining;
  const progressValue = total > 0 ? (scored / total) * 100 : 0;

  return (
    <Box className={classes.root}>
      <Typography variant="body2" className={classes.sliderLabel}>
        Missing or incorrect characters
      </Typography>
      <Box className={classes.sliderRow}>
        <Slider
          value={incorrectCharacters}
          onChange={handleSliderChange}
          min={MIN_SCORE}
          max={MAX_SCORE}
          step={1}
          valueLabelDisplay="auto"
          disabled={!hasCurrentRead || isSaving}
          aria-label="Missing or incorrect characters"
          data-testid="lpr-scoring-slider"
        />
        <Typography className={classes.sliderValue} data-testid="lpr-scoring-slider-value">
          {incorrectCharacters}
        </Typography>
      </Box>

      <Box className={classes.exceptionRow}>
        <FormControlLabel
          control={
            <Radio
              checked={isException}
              onClick={handleExceptionClick}
              color="primary"
              disabled={!hasCurrentRead || isSaving}
              data-testid="lpr-scoring-exception"
            />
          }
          label="Exception"
        />
      </Box>

      <Box className={classes.progressWrap}>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          className={classes.progressBar}
          data-testid="lpr-scoring-progress-bar"
        />
        <Box className={classes.progressText} data-testid="lpr-scoring-progress">
          {scored} scored / {remaining} remaining
        </Box>
      </Box>

      <Box className={classes.buttonRow}>
        <Button
          variant="outlined"
          size="small"
          onClick={onPrevious}
          disabled={isFirst || isSaving}
          data-testid="lpr-scoring-previous"
        >
          Previous
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={onSkip}
          disabled={!hasCurrentRead || isSaving || !canSkip}
          data-testid="lpr-scoring-skip"
        >
          Skip
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleSaveNext}
          disabled={!hasCurrentRead || isSaving || !canSave}
          startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
          data-testid="lpr-scoring-save-next"
        >
          {isSaving ? "Saving..." : "Save/Next"}
        </Button>
      </Box>
    </Box>
  );
};

export default LPRScoringControls;
